import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatJsonValue } from '~/app/utils/json';
import { parseJsonEditorText } from '../../../utils';

type UsePipelineJsonPanelOptions = {
  value: unknown;
  editable: boolean;
  disabled: boolean;
  onChange?: (value: unknown) => void;
};

export function usePipelineJsonPanel({
  value,
  editable,
  disabled,
  onChange,
}: UsePipelineJsonPanelOptions) {
  const { t } = useTranslation('pipelineDebug');
  const serialized = useMemo(
    () => (value === null || value === undefined ? '' : formatJsonValue(value)),
    [value],
  );
  const [text, setText] = useState(serialized);
  const [parseError, setParseError] = useState<string | null>(null);

  useEffect(() => {
    setText(serialized);
    setParseError(null);
  }, [serialized]);

  const hasValue = value !== null && value !== undefined;

  const applyText = useCallback(() => {
    const parsed = parseJsonEditorText(text);
    if (!parsed.success) {
      setParseError(t('messages.invalidJson'));
      return;
    }

    setParseError(null);
    onChange?.(parsed.value);
  }, [onChange, t, text]);

  const resetText = useCallback(() => {
    setText(serialized);
    setParseError(null);
  }, [serialized]);

  return {
    text,
    parseError,
    hasValue,
    setText,
    applyText,
    resetText,
    editable,
    disabled,
  };
}
